import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../../utils/api";
import ToggleSwitch from "./printers/ToggleSwitch";

const inputClass =
  "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200";

// Master list for the "Unit" dropdown on each product variant row (Single,
// Quarter, Half, Full, ...). `name` is a stable internal key; the English
// `displayName` and its per-language `translations` are what's edited here.
const VariantUnitsSection = () => {
  const [units, setUnits] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newTranslations, setNewTranslations] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [editingTranslations, setEditingTranslations] = useState({});

  const sorted = useMemo(() => {
    const list = Array.isArray(units) ? [...units] : [];
    list.sort((a, b) =>
      (a?.displayName || "").localeCompare(b?.displayName || "", undefined, {
        sensitivity: "base",
      }),
    );
    return list;
  }, [units]);

  const loadAll = async () => {
    setIsLoading(true);
    try {
      const res = await api.get("/products/variant-units/all");
      setUnits(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setUnits([]);
      toast.error("Failed to load variant units");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    api
      .get("/languages/availableLanguages")
      .then((res) =>
        setLanguages(
          (Array.isArray(res.data) ? res.data : []).filter(
            (l) => l.code !== "en",
          ),
        ),
      )
      .catch((err) => console.error("Failed loading languages", err));
  }, []);

  const replaceUnit = (updated) =>
    setUnits((prev) =>
      (prev || []).map((u) => (u._id === updated._id ? updated : u)),
    );

  const startEdit = (item) => {
    setEditingId(item._id);
    setEditingText(item.displayName || "");
    setEditingTranslations({ ...(item.translations || {}) });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
    setEditingTranslations({});
  };

  const saveEdit = async () => {
    const text = editingText.trim();
    if (!editingId || !text || isSaving) return;
    setIsSaving(true);
    try {
      const res = await api.patch(`/products/variant-units/${editingId}`, {
        displayName: text,
        translations: editingTranslations,
      });
      const updated = res.data?.unit;
      if (updated?._id) {
        replaceUnit(updated);
      } else {
        await loadAll();
      }
      toast.success("Variant unit updated");
      cancelEdit();
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to update variant unit";
      toast.error(msg);
      await loadAll();
      cancelEdit();
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async (id, nextActive) => {
    if (!id || isSaving) return;
    setIsSaving(true);
    try {
      const res = await api.patch(`/products/variant-units/${id}`, {
        isActive: !!nextActive,
      });

      const updated = res.data?.unit;
      if (updated?._id) {
        replaceUnit(updated);
      } else {
        await loadAll();
      }
    } catch (err) {
      toast.error("Failed to update variant unit");
      await loadAll();
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdd = async () => {
    const displayName = newDisplayName.trim();
    if (!displayName || isSaving) return;
    setIsSaving(true);
    try {
      const res = await api.post("/products/variant-units", {
        displayName,
        translations: newTranslations,
      });
      const created = res.data?.unit;
      toast.success("Variant unit added");
      setNewDisplayName("");
      setNewTranslations({});

      if (created?._id) {
        setUnits((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const exists = list.some((u) => u._id === created._id);
          return exists
            ? list.map((u) => (u._id === created._id ? created : u))
            : [created, ...list];
        });
      } else {
        await loadAll();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Failed to add variant unit";
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  // One input per non-English language, bound to the given map.
  const renderTranslationInputs = (values, setValues, onEnter) =>
    languages.map((lang) => (
      <div key={lang.code}>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
          {lang.name}
        </label>
        <input
          type="text"
          value={values[lang.code] || ""}
          onChange={(e) =>
            setValues((prev) => ({ ...prev, [lang.code]: e.target.value }))
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onEnter();
            }
          }}
          dir={lang.direction || "ltr"}
          placeholder={`Name in ${lang.name}`}
          className={inputClass}
        />
      </div>
    ));

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
              Add new unit (English)
            </label>
            <input
              type="text"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder="e.g. Family Pack"
              className={inputClass}
            />
          </div>
          {renderTranslationInputs(newTranslations, setNewTranslations, handleAdd)}
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={isSaving || !newDisplayName.trim()}
          className={`bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-xs font-semibold active:scale-[0.98] transition-all duration-200 ${
            isSaving || !newDisplayName.trim()
              ? "opacity-70 cursor-not-allowed"
              : ""
          }`}
        >
          + Add Unit
        </button>
      </div>

      <div className="mt-5">
        {isLoading ? (
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6">
            <p className="text-sm font-semibold text-gray-700">
              Loading variant units...
            </p>
          </div>
        ) : sorted.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 p-8 text-center">
            <p className="text-sm font-semibold text-gray-700">
              No variant units found
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Add a unit above to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sorted.map((u) => (
              <div
                key={u._id}
                className="rounded-2xl border border-gray-100 bg-white shadow-sm px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div className="min-w-0 flex-1">
                  {editingId === u._id ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5">
                            English
                          </label>
                          <input
                            type="text"
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                saveEdit();
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                cancelEdit();
                              }
                            }}
                            className={inputClass}
                            autoFocus
                          />
                        </div>
                        {renderTranslationInputs(
                          editingTranslations,
                          setEditingTranslations,
                          saveEdit,
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Press Enter or Save to save, Esc to cancel.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-gray-900 break-words">
                        {u.displayName}
                        {Object.entries(u.translations || {})
                          .filter(([, text]) => text)
                          .map(([code, text]) => (
                            <span
                              key={code}
                              className="ml-2 font-normal text-gray-600"
                              dir="auto"
                            >
                              / {text}
                            </span>
                          ))}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        name: {u.name} · {u.isActive ? "Active" : "Inactive"}
                      </p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 sm:self-auto self-end">
                  {editingId === u._id && (
                    <button
                      type="button"
                      onClick={saveEdit}
                      disabled={isSaving || !editingText.trim()}
                      className="px-3 py-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Save
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      editingId === u._id ? cancelEdit() : startEdit(u)
                    }
                    className="px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50 text-gray-700 text-xs font-semibold hover:bg-gray-100 transition"
                  >
                    {editingId === u._id ? "Cancel" : "Edit"}
                  </button>
                  <ToggleSwitch
                    checked={!!u.isActive}
                    onChange={(val) => handleToggle(u._id, val)}
                    labelOn="Active"
                    labelOff="Inactive"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VariantUnitsSection;
